import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Wand2, Loader2, Copy, Check, Sparkles, Layout, Type, Users, ArrowRight, Star,
  Mail, MessageSquareQuote, Globe, RefreshCw, Zap, ExternalLink, FileText, Rocket,
  Edit3, ArrowLeft,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.4)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const BLOCK_TYPES = [
  { type: 'marketplace_hero', label: 'Hero Section', icon: Layout, desc: 'Headlines, subtitle, CTAs' },
  { type: 'category_grid', label: 'Category Section', icon: Layout, desc: 'Section title & subtitle' },
  { type: 'featured_vendors', label: 'Featured Section', icon: Users, desc: 'Vendor showcase text' },
  { type: 'how_it_works', label: 'How It Works', icon: Layout, desc: '3-step process' },
  { type: 'community_impact', label: 'Impact Section', icon: Star, desc: 'Mission statement & stats' },
  { type: 'custom_text', label: 'Custom Text', icon: Type, desc: 'Any text content' },
  { type: 'cta_banner', label: 'CTA Banner', icon: ArrowRight, desc: 'Call-to-action copy' },
  { type: 'testimonial', label: 'Testimonial', icon: MessageSquareQuote, desc: 'Customer quote' },
  { type: 'email_signup', label: 'Email Signup', icon: Mail, desc: 'Newsletter section' },
];

export default function AICreator() {
  const [mode, setMode] = useState('full_page'); // 'full_page' | 'single_block' | 'quick_edit'
  const [selectedType, setSelectedType] = useState('marketplace_hero');
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Quick-edit mode state
  const [quickInstruction, setQuickInstruction] = useState('');
  const [quickSlug, setQuickSlug] = useState('home');
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickHistory, setQuickHistory] = useState([]);

  // Full-page mode state
  const [pagePrompt, setPagePrompt] = useState('');
  const [pageTitle, setPageTitle] = useState('');
  const [pageTarget, setPageTarget] = useState('new_page'); // 'new_page' | 'home' | 'preview'
  const [generatingPage, setGeneratingPage] = useState(false);
  const [pageResult, setPageResult] = useState(null);

  // Target selection + live preview
  const [target, setTarget] = useState('marketplace'); // 'marketplace' | site_id
  const [sites, setSites] = useState([]);
  const [previewKey, setPreviewKey] = useState(0); // forces iframe reload
  const [applying, setApplying] = useState(false);

  // In-preview navigation history (for Back button)
  const [previewHistory, setPreviewHistory] = useState([]);
  const [currentPreviewUrl, setCurrentPreviewUrl] = useState(null);

  const token = localStorage.getItem('masterToken');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const loadSites = async () => {
      try {
        const res = await axios.get(`${API}/master/sites`, { headers: authHeaders });
        setSites(res.data || []);
      } catch { /* silent */ }
    };
    loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedSite = sites.find((s) => s.site_id === target);
  const previewUrl = target === 'marketplace'
    ? process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '') + '/'
    : selectedSite?.slug
      ? `${process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '')}/s/${selectedSite.slug}`
      : null;

  const handleGenerate = async () => {
    if (!prompt.trim()) return toast.error('Enter a prompt');
    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/ai/generate-content`,
        { block_type: selectedType, prompt: prompt.trim(), context: context.trim() },
        { headers: authHeaders },
      );
      setResult(res.data);
      toast.success('Content generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleGeneratePage = async () => {
    if (!pagePrompt.trim()) return toast.error('Describe the page you want');
    if (pageTarget === 'new_page' && !pageTitle.trim()) return toast.error('Enter a page title');
    setGeneratingPage(true);
    setPageResult(null);
    try {
      const res = await axios.post(
        `${API}/ai/generate-page`,
        {
          prompt: pagePrompt.trim(),
          context: context.trim(),
          page_title: pageTitle.trim(),
          target: pageTarget,
        },
        { headers: authHeaders },
      );
      setPageResult(res.data);
      toast.success(
        pageTarget === 'new_page'
          ? `✨ New page created with ${res.data.count} blocks!`
          : pageTarget === 'home'
            ? `✨ Homepage replaced with ${res.data.count} new blocks — refresh preview`
            : `Generated ${res.data.count} blocks`
      );
      if (pageTarget !== 'preview') setPreviewKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Page generation failed');
    } finally {
      setGeneratingPage(false);
    }
  };

  const handleQuickEdit = async () => {
    if (!quickInstruction.trim()) return toast.error('Type an instruction');
    setQuickLoading(true);
    try {
      const res = await axios.post(
        `${API}/ai/quick-edit`,
        {
          slug: quickSlug.trim() || 'home',
          instruction: quickInstruction.trim(),
          target,
        },
        { headers: authHeaders },
      );
      const summary = res.data?.summary || 'Edit applied';
      setQuickHistory((h) => [{ summary, instruction: quickInstruction.trim(), at: new Date().toISOString() }, ...h].slice(0, 20));
      setQuickInstruction('');
      toast.success('✨ ' + summary);
      // Refresh preview to see the change
      setPreviewKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Quick edit failed');
    } finally {
      setQuickLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result?.content) {
      navigator.clipboard.writeText(JSON.stringify(result.content, null, 2));
      setCopied(true);
      toast.success('Copied to clipboard! Paste into the block editor.');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Track iframe navigation for the in-preview Back button
  useEffect(() => {
    const iframe = document.querySelector('iframe[data-testid="preview-iframe"]');
    if (!iframe) return;
    const onLoad = () => {
      try {
        const u = iframe.contentWindow?.location?.href;
        if (!u) return;
        setCurrentPreviewUrl(u);
        setPreviewHistory((h) => {
          if (h.length && h[h.length - 1] === u) return h;
          return [...h, u].slice(-30);
        });
      } catch {
        // cross-origin — can't peek location. Swallow.
      }
    };
    iframe.addEventListener('load', onLoad);
    return () => iframe.removeEventListener('load', onLoad);
  }, [previewKey, target]);

  const goBackInPreview = () => {
    const iframe = document.querySelector('iframe[data-testid="preview-iframe"]');
    if (!iframe) return;
    // Peel the latest entry (current page) and navigate to the previous one
    setPreviewHistory((h) => {
      if (h.length < 2) return h;
      const next = h.slice(0, -1);
      try { iframe.contentWindow.location.href = next[next.length - 1]; } catch {}
      return next;
    });
  };

  const applyToTarget = async () => {
    if (!result?.content) return;
    setApplying(true);
    try {
      if (target === 'marketplace') {
        // Load current homepage blocks
        const pageRes = await axios.get(`${API}/master/marketplace/pages/home`, { headers: authHeaders });
        const blocks = pageRes.data?.blocks || [];
        const idx = blocks.findIndex((b) => b.type === selectedType);
        if (idx >= 0) {
          blocks[idx] = { ...blocks[idx], content: { ...(blocks[idx].content || {}), ...result.content } };
        } else {
          blocks.push({ id: `${selectedType}-${Date.now()}`, type: selectedType, order: blocks.length, is_visible: true, content: result.content });
        }
        await axios.put(`${API}/master/marketplace/pages/home`, { blocks }, { headers: authHeaders });
      } else {
        // Apply to client site
        const siteRes = await axios.get(`${API}/master/sites/${target}/content`, { headers: authHeaders });
        const blocks = siteRes.data?.blocks || [];
        const idx = blocks.findIndex((b) => b.type === selectedType);
        if (idx >= 0) {
          blocks[idx] = { ...blocks[idx], content: { ...(blocks[idx].content || {}), ...result.content } };
        } else {
          blocks.push({ id: `${selectedType}-${Date.now()}`, type: selectedType, order: blocks.length, is_visible: true, content: result.content });
        }
        await axios.put(`${API}/master/sites/${target}/content`, { blocks }, { headers: authHeaders });
      }
      toast.success(`✨ Applied to ${targetLabel}! Preview refreshing…`);
      setPreviewKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const targetLabel = target === 'marketplace'
    ? 'Marketplace Homepage'
    : selectedSite?.site_name || selectedSite?.business_name || 'Client Website';

  return (
    <div className="space-y-6" data-testid="ai-creator">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Sparkles size={24} className="text-[#A78BFA]" /> AI Content Creator
        </h1>
        <p className="text-sm text-[#9CA3AF] mt-1">Generate marketplace or client-site content with GPT-5.2 — with live preview and one-click apply</p>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10" data-testid="ai-mode-toggle">
        <button
          onClick={() => setMode('full_page')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition ${mode === 'full_page' ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white' : 'text-[#9CA3AF] hover:text-white'}`}
          data-testid="ai-mode-full-page"
        >
          <Rocket size={13} /> Generate full page
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-white/20">NEW</span>
        </button>
        <button
          onClick={() => setMode('single_block')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition ${mode === 'single_block' ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white' : 'text-[#9CA3AF] hover:text-white'}`}
          data-testid="ai-mode-single-block"
        >
          <Layout size={13} /> Single block
        </button>
        <button
          onClick={() => setMode('quick_edit')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition ${mode === 'quick_edit' ? 'bg-gradient-to-r from-[#F59E0B] to-[#FCD34D] text-black' : 'text-[#9CA3AF] hover:text-white'}`}
          data-testid="ai-mode-quick-edit"
        >
          <Edit3 size={13} /> Quick edit
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-black/15">NEW</span>
        </button>
      </div>

      {/* Target Selector */}
      <GlassCard className="p-5" data-testid="target-selector">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-[#A78BFA]" />
            <span className="text-white text-sm font-semibold">Target:</span>
          </div>
          <select
            value={target}
            onChange={(e) => { setTarget(e.target.value); setPreviewKey((k) => k + 1); }}
            className="flex-1 bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
            data-testid="target-select"
          >
            <option value="marketplace">🏪 Marketplace Homepage (/)</option>
            {sites.length > 0 && <optgroup label="Client Websites">
              {sites.map((s) => (
                <option key={s.site_id} value={s.site_id}>
                  {s.site_name || s.business_name || s.slug} — {s.owner_email || 'unknown'}
                </option>
              ))}
            </optgroup>}
          </select>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewKey((k) => k + 1)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-[#9CA3AF] hover:text-white text-xs"
              data-testid="refresh-preview"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-[#9CA3AF] hover:text-white text-xs"
              >
                <ExternalLink size={12} /> Open
              </a>
            )}
          </div>
        </div>
        <p className="text-[#64748B] text-[11px] mt-3">
          Selected: <span className="text-[#A78BFA] font-semibold">{targetLabel}</span> — AI generation + live Apply works for the marketplace homepage and every client site.
        </p>
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Panel */}
        <div className="space-y-5 lg:col-span-1">
          {mode === 'quick_edit' ? (
            <GlassCard className="p-5 space-y-3" data-testid="quick-edit-panel">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Edit3 size={14} className="text-[#FCD34D]" /> Quick edit · plain English
              </h2>
              <p className="text-[#64748B] text-[11px] leading-relaxed">
                Make targeted changes without rebuilding a block. Change a headline, reorder sections, hide a block, or update copy. Examples below.
              </p>

              <div>
                <Label className="text-[#9CA3AF] text-xs">Page slug</Label>
                <Input
                  value={quickSlug}
                  onChange={(e) => setQuickSlug(e.target.value)}
                  placeholder="home"
                  className="bg-white/5 border-white/10 text-white rounded-xl h-9 text-sm mt-1 font-mono"
                  data-testid="quick-edit-slug"
                />
                <p className="text-[#64748B] text-[10px] mt-1">
                  'home' for the marketplace homepage · any page slug for custom pages
                </p>
              </div>

              <div>
                <Label className="text-[#9CA3AF] text-xs">What should change?</Label>
                <Textarea
                  value={quickInstruction}
                  onChange={(e) => setQuickInstruction(e.target.value)}
                  placeholder={"e.g., Change the hero headline to 'Hire Indigenous, Ship Worldwide'\n\nOr: Move the featured vendors above the category grid\n\nOr: Hide the testimonial section"}
                  className="bg-white/5 border-white/10 text-white rounded-xl text-sm mt-1 min-h-[110px] placeholder:text-white/20"
                  data-testid="quick-edit-instruction"
                  onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleQuickEdit(); }}
                />
              </div>

              <button
                onClick={handleQuickEdit}
                disabled={quickLoading}
                className="w-full py-2.5 rounded-xl font-bold text-black bg-gradient-to-r from-[#F59E0B] to-[#FCD34D] shadow-[0_0_20px_rgba(245,158,11,0.3)] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                data-testid="quick-edit-submit"
              >
                {quickLoading ? <><Loader2 size={14} className="animate-spin" /> Applying…</> : <><Zap size={14} /> Apply edit (⌘/Ctrl+Enter)</>}
              </button>

              {quickHistory.length > 0 && (
                <div className="pt-3 border-t border-white/5">
                  <p className="text-[#A78BFA] text-[10px] uppercase tracking-wider font-bold mb-2">Recent edits</p>
                  <ul className="space-y-1.5 text-[11px] max-h-48 overflow-y-auto" data-testid="quick-edit-history">
                    {quickHistory.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#D1D5DB] leading-snug">
                        <Check size={11} className="text-[#10B981] flex-shrink-0 mt-0.5" />
                        <div>
                          <div>{h.summary}</div>
                          <div className="text-[#64748B] italic">"{h.instruction.slice(0, 72)}{h.instruction.length > 72 ? '…' : ''}"</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </GlassCard>
          ) : mode === 'full_page' ? (
            <GlassCard className="p-5 space-y-3" data-testid="full-page-panel">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Rocket size={14} className="text-[#A78BFA]" /> Describe the page
              </h2>
              <p className="text-[#64748B] text-[11px]">
                AI will design a complete 4-7 block page — hero, body sections, and a CTA — pre-wired and ready.
              </p>

              <div>
                <Label className="text-[#9CA3AF] text-xs">Prompt *</Label>
                <Textarea
                  value={pagePrompt}
                  onChange={(e) => setPagePrompt(e.target.value)}
                  placeholder="e.g., A 'How we support Indigenous women entrepreneurs' page — tell the story of our commitment, show 3 ways we help, and end with a 'Join us' CTA."
                  className="bg-white/5 border-white/10 text-white rounded-xl text-sm mt-1 min-h-[120px] placeholder:text-white/20"
                  data-testid="ai-page-prompt"
                />
              </div>

              <div>
                <Label className="text-[#9CA3AF] text-xs">Extra Context (optional)</Label>
                <Input
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="Target audience, tone, key stats…"
                  className="bg-white/5 border-white/10 text-white rounded-xl h-9 text-sm mt-1 placeholder:text-white/20"
                />
              </div>

              <div>
                <Label className="text-[#9CA3AF] text-xs">Where should this live?</Label>
                <select
                  value={pageTarget}
                  onChange={(e) => setPageTarget(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm mt-1"
                  data-testid="ai-page-target"
                >
                  <option value="new_page">📄 Create as new page (/p/your-slug)</option>
                  <option value="home">🏠 Replace marketplace HOME</option>
                  <option value="preview">👀 Preview only (no save)</option>
                </select>
              </div>

              {pageTarget === 'new_page' && (
                <div>
                  <Label className="text-[#9CA3AF] text-xs">Page title *</Label>
                  <Input
                    value={pageTitle}
                    onChange={(e) => setPageTitle(e.target.value)}
                    placeholder="e.g., Supporting Indigenous Women"
                    className="bg-white/5 border-white/10 text-white rounded-xl h-9 text-sm mt-1 placeholder:text-white/20"
                    data-testid="ai-page-title"
                  />
                </div>
              )}

              <button
                onClick={handleGeneratePage}
                disabled={generatingPage}
                className="w-full py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                data-testid="ai-generate-page-btn"
              >
                {generatingPage ? <><Loader2 size={16} className="animate-spin" /> Building page…</> : <><Rocket size={16} /> Generate {pageTarget === 'new_page' ? 'new page' : pageTarget === 'home' ? 'homepage' : 'preview'}</>}
              </button>

              {pageResult && (
                <div className="mt-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 space-y-2" data-testid="ai-page-result">
                  <p className="text-emerald-200 text-xs font-semibold">
                    ✓ Generated {pageResult.count} blocks
                    {pageResult.page && (
                      <>
                        {' · status: '}<strong>{pageResult.status === 'draft_pending_approval' ? 'DRAFT' : 'LIVE'}</strong>
                      </>
                    )}
                  </p>
                  <ul className="text-[10px] text-emerald-100/80 space-y-0.5 max-h-32 overflow-y-auto">
                    {pageResult.blocks.map((b, i) => (
                      <li key={b.id}>
                        <span className="font-mono opacity-60">#{i + 1}</span> {b.type}
                      </li>
                    ))}
                  </ul>
                  {pageResult.page?.slug && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-emerald-500/20">
                      <a
                        href={`/p/${pageResult.page.slug}?preview=1`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/10 text-white text-[11px] font-semibold hover:bg-white/20"
                        data-testid="ai-preview-draft"
                      >
                        <ExternalLink size={11} /> Preview draft
                      </a>
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/master/marketplace/pages/${pageResult.page.slug}/publish`, {}, { headers: authHeaders });
                            toast.success('✨ Page published — now live at /p/' + pageResult.page.slug);
                            setPageResult({ ...pageResult, status: 'published', page: { ...pageResult.page, is_published: true } });
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Publish failed');
                          }
                        }}
                        disabled={pageResult.status === 'published'}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#10B981] to-[#059669] text-white text-[11px] font-bold disabled:opacity-40"
                        data-testid="ai-approve-publish"
                      >
                        <Check size={11} /> {pageResult.status === 'published' ? 'Published' : 'Approve & publish'}
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Discard this draft?')) return;
                          try {
                            await axios.delete(`${API}/master/marketplace/pages/${pageResult.page.slug}`, { headers: authHeaders });
                            toast.success('Draft discarded');
                            setPageResult(null);
                            setPagePrompt('');
                            setPageTitle('');
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Delete failed');
                          }
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 text-[#9CA3AF] text-[11px] font-semibold hover:bg-red-500/20 hover:text-red-300"
                        data-testid="ai-reject-draft"
                      >
                        Reject & delete
                      </button>
                    </div>
                  )}
                  {pageResult.page?.slug && (
                    <a href={`/master/marketplace/pages`} className="inline-block text-[10px] font-semibold text-emerald-200 underline">
                      Open in editor to fine-tune →
                    </a>
                  )}
                </div>
              )}
            </GlassCard>
          ) : (
            <>
              <GlassCard className="p-5">
                <h2 className="text-sm font-semibold text-white mb-3">1. Choose Block Type</h2>
                <div className="grid grid-cols-2 gap-2">
                  {BLOCK_TYPES.map((bt) => {
                    const Icon = bt.icon;
                    return (
                      <button key={bt.type} onClick={() => setSelectedType(bt.type)} className={`p-2.5 rounded-xl text-left transition-all text-xs ${selectedType === bt.type ? 'bg-[#8B5CF6]/20 border border-[#8B5CF6]/40 text-white' : 'bg-white/5 border border-white/5 text-[#9CA3AF] hover:border-white/10'}`} data-testid={`ai-block-${bt.type}`}>
                        <Icon size={13} className={selectedType === bt.type ? 'text-[#A78BFA] mb-1' : 'text-[#64748B] mb-1'} />
                        <p className="font-medium leading-tight">{bt.label}</p>
                      </button>
                    );
                  })}
                </div>
              </GlassCard>

          <GlassCard className="p-5 space-y-3">
            <h2 className="text-sm font-semibold text-white">2. Describe What You Want</h2>
            <div>
              <Label className="text-[#9CA3AF] text-xs">Prompt</Label>
              <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g., Write a hero that champions Indigenous entrepreneurs with a strong call to action" className="bg-white/5 border-white/10 text-white rounded-xl text-sm mt-1 min-h-[90px] placeholder:text-white/20" data-testid="ai-prompt" />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-xs">Extra Context (optional)</Label>
              <Input value={context} onChange={(e) => setContext(e.target.value)} placeholder="Target audience, tone…" className="bg-white/5 border-white/10 text-white rounded-xl h-9 text-sm mt-1 placeholder:text-white/20" />
            </div>
            <button onClick={handleGenerate} disabled={generating} className="w-full py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all flex items-center justify-center gap-2" data-testid="ai-generate-btn">
              {generating ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : <><Wand2 size={16} /> Generate</>}
            </button>
          </GlassCard>

          {result && (
            <GlassCard className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-white">3. Apply or Copy</h2>
                <button onClick={copyToClipboard} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 text-[#9CA3AF] hover:text-white transition-all" data-testid="ai-copy-btn">
                  {copied ? <><Check size={12} className="text-[#86EFAC]" /> Copied</> : <><Copy size={12} /> Copy JSON</>}
                </button>
              </div>
              <button
                onClick={applyToTarget}
                disabled={applying}
                className="w-full py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-[#10B981] to-[#059669] shadow-[0_0_20px_rgba(16,185,129,0.3)] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                data-testid="apply-to-target"
              >
                {applying ? <><Loader2 size={14} className="animate-spin" /> Applying…</> : <><Zap size={14} /> Apply to {targetLabel}</>}
              </button>
              <p className="text-[#64748B] text-[10px]">The block of the same type will be merged on the page, or appended if not present. Preview refreshes automatically.</p>
            </GlassCard>
          )}
            </>
          )}
        </div>

        {/* Live Preview Panel */}
        <div className="lg:col-span-2">
          <GlassCard className="p-0 overflow-hidden h-full flex flex-col">
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={goBackInPreview}
                  disabled={previewHistory.length < 2}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[#9CA3AF] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs"
                  title="Go back to previous page in preview"
                  data-testid="preview-back-btn"
                >
                  <ArrowLeft size={12} /> Back
                </button>
                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Globe size={14} className="text-[#A78BFA]" /> Live Preview — {targetLabel}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {currentPreviewUrl && (
                  <span className="text-[10px] text-[#64748B] font-mono max-w-[220px] truncate" title={currentPreviewUrl} data-testid="preview-current-url">
                    {currentPreviewUrl.replace(/^https?:\/\/[^/]+/, '') || '/'}
                  </span>
                )}
                {generating && <Loader2 size={14} className="animate-spin text-[#8B5CF6]" />}
              </div>
            </div>
            <div className="flex-1 bg-white min-h-[600px]">
              {previewUrl ? (
                <iframe
                  key={previewKey}
                  src={previewUrl}
                  title="Live Preview"
                  className="w-full h-full min-h-[600px]"
                  sandbox="allow-same-origin allow-scripts allow-popups"
                  data-testid="preview-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-[600px] text-gray-400 text-sm">
                  Preview not available for this site
                </div>
              )}
            </div>
            {result && (
              <div className="p-4 border-t border-white/5 bg-black/30">
                <p className="text-[#A78BFA] text-[10px] uppercase tracking-wider font-bold mb-2">Generated Content Preview</p>
                <div className="text-[#9CA3AF] text-xs space-y-1 max-h-32 overflow-y-auto">
                  {Object.entries(result.content || {}).map(([key, value]) => (
                    <div key={key}>
                      <span className="text-[#64748B]">{key}:</span>{' '}
                      <span className="text-white">{Array.isArray(value) ? `${value.length} items` : String(value).slice(0, 80)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </GlassCard>
        </div>
      </div>
    </div>
  );
}
